import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBuildById } from '@/features/builds/data/builds';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { getUploadById } from '@/features/uploads/data';
import { NotFoundScreen } from '@/pages/NotFoundScreen';
import { queryKeys, useAccessToken } from '@/shared/hooks';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { UploadDetailContent } from './UploadDetailContent';
import type { AuthSession } from '@/auth/types';
import type { ApiError } from '@/shared/data/client';
import type { BuildDto, UploadDto } from '@flowqa/shared';

type UploadDetailPageProps = { session: AuthSession | null };

export const UploadDetailPage = ({ session }: UploadDetailPageProps): ReactElement => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const token = useAccessToken();
  const searchId = useMemo(() => (id ?? '').trim(), [id]);

  const uploadQuery = useQuery<UploadDto, ApiError>({
    queryKey: queryKeys.uploadDetail(searchId || null, selectedOrganisationId ?? null),
    enabled: Boolean(token && searchId),
    queryFn: async () => await getUploadById(searchId, selectedOrganisationId ?? undefined),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'processing' ? 5000 : false;
    },
  });

  const { data: upload, isPending, isError, error } = uploadQuery;
  const buildId = upload?.buildId ?? null;
  const buildQuery = useQuery<BuildDto | null, ApiError>({
    queryKey: queryKeys.buildDetail(buildId, selectedOrganisationId ?? null),
    enabled: Boolean(token && buildId),
    queryFn: async () =>
      buildId ? await getBuildById(buildId, selectedOrganisationId ?? undefined) : null,
  });
  const build = buildQuery.data ?? null;

  const hasInvalidId = !searchId;
  const showNotFound = hasInvalidId || (!isPending && uploadQuery.isFetched && !upload);

  useEffect(() => {
    if (!upload) return;
    const uploadProjectId = upload.projectId;
    if (selectedProjectId && uploadProjectId !== selectedProjectId) {
      void navigate('/execution-runs', { replace: true });
      return;
    }
    if (!selectedProjectId) {
      setSelectedProjectId(uploadProjectId);
    }
  }, [upload, navigate, selectedProjectId, setSelectedProjectId]);

  if (showNotFound) {
    return (
      <NotFoundScreen
        title="Upload not found"
        body="This upload does not exist or you no longer have access to it."
        backTo="/execution-runs"
        backLabel="Back to execution runs"
      />
    );
  }

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view upload details."
      organisationId={selectedOrganisationId}
      requireProject={false}
      projectMessage="Select a project to view upload details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load upload"
      data={upload}
      loadingFallback={<></>}
    >
      {(resolvedUpload) => {
        return (
          <UploadDetailContent
            title={resolvedUpload.fileName}
            upload={resolvedUpload}
            build={build}
          />
        );
      }}
    </DetailPageScaffold>
  );
};
